import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

const defaultIconSx = {
  fontSize: 25,
  color: '#6FA594',
  flexShrink: 0,
};

const formatDateDisplay = (value) => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  return String(value);
};

const formatTimeDisplay = (value) => {
  if (!value) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  const raw = String(value).trim();
  if (!raw) return '';
  if (/\b(?:am|pm)\b/i.test(raw)) return raw;

  const timeMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!timeMatch) return raw;

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return raw;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const LogFieldCard = ({
  label,
  value,
  placeholder = '',
  icon: Icon,
  onClick,
  showLabel = true,
  kind = 'text',
  sx = {},
  valueSx = {},
  labelSx = {},
  iconSx = {},
  ariaLabel,
  disabled = false,
}) => {
  const displayValue = kind === 'date'
    ? formatDateDisplay(value) || placeholder
    : kind === 'time'
      ? formatTimeDisplay(value) || placeholder
      : value || placeholder;
  const isPlaceholder = !value;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || `${String(label || 'field').toLowerCase()} selector`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
        minHeight: 88,
        px: 1.45,
        py: 1.25,
        borderRadius: '22px',
        border: '1.5px solid #DCE8E2',
        bgcolor: '#F2F7F4',
        boxSizing: 'border-box',
        boxShadow: 'none',
        textAlign: 'left',
        transition: 'all 160ms ease',
        '&:hover': {
          borderColor: '#C9DED5',
          transform: disabled ? 'none' : 'translateY(-1px)',
        },
        '&.Mui-disabled': {
          opacity: 0.7,
        },
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0, textAlign: 'left', flex: '1 1 auto' }}>
        {showLabel ? (
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 800,
              color: '#8A9A94',
              letterSpacing: '0.11em',
              textTransform: 'uppercase',
              lineHeight: 1.15,
              mb: 0.5,
              ...labelSx,
            }}
          >
            {label}
          </Typography>
        ) : null}
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 800,
            color: isPlaceholder ? '#8B9490' : '#1A2320',
            lineHeight: 1.25,
            ...valueSx,
          }}
        >
          {displayValue}
        </Typography>
      </Box>
      {Icon ? <Icon sx={{ ...defaultIconSx, ...iconSx }} /> : null}
    </ButtonBase>
  );
};

export default LogFieldCard;
